import {inject, Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import {catchError, filter, map, Observable, of, take, tap} from 'rxjs';
import {WELCOME} from './shared/constants';
import {SessionState} from './shared/enum/session-state.enum';
import {SessionService} from './shared/services/session.service';
import {AuthService} from './shared/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class AsmGuard implements CanActivate, CanActivateChild {
  // INJECT
  private authService = inject(AuthService);
  private sessionService = inject(SessionService);
  private router = inject(Router);

  canEnter(requestedUrl: string): Observable<boolean> {
    return this.sessionService.sessionStates$.pipe(
      filter((states) => states.length > 0),
      take(1),
      map((states) => states.includes(SessionState.ASMAGENT)),
      tap((isAsmAgent) => {
        if (!isAsmAgent) {
          this.loginWithRedirect(requestedUrl);
        }
      }),
      catchError(() => {
        this.loginWithRedirect(requestedUrl);
        return of(false);
      }),
    );
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | Observable<boolean> | Promise<boolean> {
    return this.canEnter(state.url);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | Observable<boolean> | Promise<boolean> {
    return this.canEnter(state.url);
  }

  private loginWithRedirect(requestedUrl: string) {
    // A refused sign-in has to land somewhere that explains itself, otherwise
    // the rejection is an unhandled error and the user sees nothing.
    this.authService.login(requestedUrl).subscribe({
      error: () => this.router.navigate([WELCOME]),
    });
  }
}
